import { logGAEvent } from "@/components/GoogleAnalytics";

const EVENT_CATEGORY = "Booking_Flow";

type TrackName = string;

/** Which control opened/closed the sticky summary panel. */
export type StickySummaryToggleTrigger = "cart_icon" | "chevron" | "bar" | "minimise";

/** Every user interaction with the sticky summary bar and its expanded panel. */
export type StickySummaryInteraction =
  | { action: "summary_open" | "summary_close"; trigger: StickySummaryToggleTrigger }
  | { action: "back" | "next"; label: string }
  | { action: "checkout" }
  | {
      action: "track_tab_select" | "session_click" | "remove_session_click" | "remove_session_confirm" | "remove_session_cancel";
      /** Summary track key (MAIN, BK, MINI, ...) the interaction targets. */
      summaryTrack: string;
    };

/** Page/summary state at the moment of the interaction (before it takes effect). */
export type StickySummaryContext = {
  route: string;
  screen: string;
  track?: TrackName;
  device: "mobile" | "desktop";
  isExpanded: boolean;
  cartItemCount: number;
};

/** Every step of using an Active Kids voucher, from the seats-and-races card to the outcome. */
export type ActiveKidsVoucherAction =
  | {
      action: "opt_in_click";
      /** True when the card is reopened to manage a voucher already applied. */
      alreadyApplied: boolean;
    }
  | {
      action: "apply_success";
      voucherCount: number;
      /** Combined balance across every voucher on the session. */
      balanceCents: number;
      alsoInAnotherCart: boolean;
    }
  | { action: "apply_failure"; reason: string }
  | { action: "remove"; voucherCount: number; balanceCents: number }
  | { action: "continue_without" };

export const BookingAnalytics = {
  /**
   * Fires when the router renders a new booking step.
   * Builds an automatic funnel: Track Selection → Config → Postcode → Date/Time → Review → Cart.
   */
  stepViewed(step: {
    route: string;
    screen: string;
    track?: TrackName;
  }) {
    logGAEvent("booking_step_view", {
      event_category: EVENT_CATEGORY,
      booking_route: step.route,
      booking_screen: step.screen,
      selected_track: step.track ?? "none",
    });
  },

  /** User selects (or toggles) a track/experience on the TrackSelection screen. */
  trackSelected(track: TrackName) {
    logGAEvent("track_selected", {
      event_category: EVENT_CATEGORY,
      selected_track: track,
    });
  },

  /** User selects an age group on the RacingTrackSelection screen. */
  ageGroupSelected(ageGroup: string, track: TrackName) {
    logGAEvent("age_group_selected", {
      event_category: EVENT_CATEGORY,
      age_group: ageGroup,
      selected_track: track,
    });
  },

  /** User configures seats/races (or players/sessions) and proceeds. */
  bookingConfigured(config: {
    track: TrackName;
    seats?: number;
    races?: number;
    players?: number;
    sessions?: number;
  }) {
    logGAEvent("booking_configured", {
      event_category: EVENT_CATEGORY,
      selected_track: config.track,
      seats: config.seats,
      races: config.races,
      players: config.players,
      sessions: config.sessions,
    });
  },

  /** User submits a valid postcode. */
  postcodeSubmitted(postcode: string, distance: number, track: TrackName) {
    logGAEvent("postcode_submitted", {
      event_category: EVENT_CATEGORY,
      postcode,
      distance_km: distance,
      selected_track: track,
    });
  },

  /** User selects a time slot. */
  timeSlotSelected(slot: {
    track: TrackName;
    slotTime: string;
    price: string | number;
    date?: string;
  }) {
    logGAEvent("time_slot_selected", {
      event_category: EVENT_CATEGORY,
      selected_track: slot.track,
      slot_time: slot.slotTime,
      slot_price: slot.price,
      selected_date: slot.date ?? "",
    });
  },

  /** User deselects a previously chosen time slot. */
  timeSlotDeselected(slot: {
    track: TrackName;
    slotTime: string;
  }) {
    logGAEvent("time_slot_deselected", {
      event_category: EVENT_CATEGORY,
      selected_track: slot.track,
      slot_time: slot.slotTime,
    });
  },

  /** Add-to-cart succeeded. */
  addToCartSuccess(details: {
    track: TrackName;
    quantity: number;
    variantCount: number;
    source: "review_experience" | "add_another_attraction" | "bottom_summary";
  }) {
    logGAEvent("add_to_cart", {
      event_category: EVENT_CATEGORY,
      selected_track: details.track,
      quantity: details.quantity,
      variant_count: details.variantCount,
      source: details.source,
      success: true,
    });
  },

  /** Add-to-cart failed. */
  addToCartFailure(details: {
    track: TrackName;
    reason: string;
    source: "review_experience" | "add_another_attraction" | "bottom_summary";
  }) {
    logGAEvent("add_to_cart", {
      event_category: EVENT_CATEGORY,
      selected_track: details.track,
      source: details.source,
      success: false,
      failure_reason: details.reason,
    });
  },

  /** User clicks "Book Now" to go to checkout. */
  beginCheckout(track: TrackName) {
    logGAEvent("begin_checkout", {
      event_category: EVENT_CATEGORY,
      selected_track: track,
    });
  },

  /**
   * Any click in the sticky summary (expand/collapse, nav buttons, track tabs, sessions, remove).
   * One event name keeps GA reporting simple - segment by `interaction_action`.
   */
  stickySummaryInteraction(interaction: StickySummaryInteraction, context: StickySummaryContext) {
    logGAEvent("sticky_summary_interaction", {
      event_category: EVENT_CATEGORY,
      interaction_action: interaction.action,
      interaction_trigger: "trigger" in interaction ? interaction.trigger : undefined,
      button_label: "label" in interaction ? interaction.label : undefined,
      summary_track: "summaryTrack" in interaction ? interaction.summaryTrack : undefined,
      booking_route: context.route,
      booking_screen: context.screen,
      selected_track: context.track ?? "none",
      device_type: context.device,
      summary_expanded: context.isExpanded,
      cart_item_count: context.cartItemCount,
    });
  },

  /** User clicks "Add Another Attraction" after a successful add-to-cart. */
  addAnotherAttraction(track: TrackName) {
    logGAEvent("add_another_attraction", {
      event_category: EVENT_CATEGORY,
      completed_track: track,
    });
  },

  /**
   * Any Active Kids voucher interaction, tagged with the track it happened on.
   * One event name, like the sticky summary - segment by `voucher_action` and `selected_track`.
   * The voucher code itself is never sent.
   */
  activeKidsVoucher(event: ActiveKidsVoucherAction, track: TrackName) {
    logGAEvent("active_kids_voucher", {
      event_category: EVENT_CATEGORY,
      voucher_action: event.action,
      selected_track: track,
      already_applied: "alreadyApplied" in event ? event.alreadyApplied : undefined,
      voucher_count: "voucherCount" in event ? event.voucherCount : undefined,
      voucher_balance: "balanceCents" in event ? event.balanceCents / 100 : undefined,
      currency: "balanceCents" in event ? "AUD" : undefined,
      also_in_another_cart: "alsoInAnotherCart" in event ? event.alsoInAnotherCart : undefined,
      failure_reason: "reason" in event ? event.reason : undefined,
    });
  },
};
